// Interfaces Section of the Agent page (agent.md). Named for the domain noun
// `connection` (docs/09 §7/§8 noun parity — §2.13 "Interfaces" is UI copy
// only). Keyset list + the interface editor Drawer (create rides
// `?new=connection`, edit rides `?connection=<uuid>`; params owned by the
// Agent route container). Only capability-projected running transports are
// authorable (§2.13); sip/legacy-websocket rows render read-only (no Edit) but
// keep Delete. GET /v1/me `connection_capabilities` gates every authorable
// option. Container owns queries + mutations;
// `AgentConnectionsView` is props-only for tests/stories.
import type { SyntheticEvent } from "react";
import { useEffect, useMemo, useState } from "react";

import {
  useMutation,
  useQuery,
  useQueryClient,
  type InfiniteData,
} from "@tanstack/react-query";
import type { ColumnDef } from "@tanstack/react-table";

import type { components } from "../../api/schema";
import { apiClient } from "../../lib/apiClient";
import { ApiRequestError, requestErrorDescription } from "../../lib/errors";
import { queryKeys } from "../../lib/queryKeys";
import { isShowcaseMode } from "../../lib/showcaseMode";
import {
  useListQuery,
  type ListQueryPage,
  type ListQueryResult,
} from "../../lib/useListQuery";
import {
  Badge,
  Button,
  Code,
  Drawer,
  EmptyState,
  Inline,
  InlineError,
  Section,
  Select,
  Skeleton,
  Stack,
  Text,
  TextInput,
  useToast,
} from "../../ui";
import { PaginatedTableSection } from "../shared/PaginatedTableSection";
import { DateCell } from "../shared/DateCell";
import { NameWithId } from "../shared/NameWithId";
import { RowConfirm } from "../shared/RowConfirm";
import { ShowcaseReadOnlyNotice } from "../ShowcaseReadOnlyNotice";
import { HttpInterfaceFields } from "./interfaceFields/HttpInterfaceFields";
import { LiveKitInterfaceFields } from "./interfaceFields/LiveKitInterfaceFields";
import { PipecatCloudInterfaceFields } from "./interfaceFields/PipecatCloudInterfaceFields";
import { TelephonyInterfaceFields } from "./interfaceFields/TelephonyInterfaceFields";
import { WebsocketInterfaceFields } from "./interfaceFields/WebsocketInterfaceFields";
import {
  isEditableConnection,
  isRunningInterface,
  interfaceLabel,
  targetLabel,
} from "./lib/connectionConfig";
import {
  buildConnectionSubmission,
  configPreview,
  draftFromConnection,
  emptyDraft,
  type ConnectionFormSubmission,
  type InterfaceKind,
} from "./lib/connectionDraft";

type ConnectionCreate = components["schemas"]["ConnectionCreate"];
type ConnectionCapability =
  components["schemas"]["ConnectionCapabilityResponse"];
type ConnectionPatch = components["schemas"]["ConnectionPatch"];
type ConnectionResponse = components["schemas"]["ConnectionResponse"];

interface InterfaceOption {
  kind: InterfaceKind;
  label: string;
  modality: string;
  provider: string;
  transport: string;
}

const interfaceOptionCatalog: readonly InterfaceOption[] = [
  {
    kind: "http",
    label: "Chat (HTTP)",
    modality: "text",
    provider: "direct",
    transport: "http",
  },
  {
    kind: "websocket_chat",
    label: "Chat (direct WebSocket)",
    modality: "text",
    provider: "direct",
    transport: "websocket",
  },
  {
    kind: "websocket_voice",
    label: "Voice (direct WebSocket)",
    modality: "voice",
    provider: "direct",
    transport: "websocket",
  },
  {
    kind: "twilio",
    label: "Voice — inbound agent",
    modality: "voice",
    provider: "twilio",
    transport: "telephony",
  },
  {
    kind: "livekit_voice",
    label: "Voice (LiveKit WebRTC)",
    modality: "voice",
    provider: "livekit",
    transport: "webrtc",
  },
  {
    kind: "livekit_chat",
    label: "Chat (LiveKit data)",
    modality: "text",
    provider: "livekit",
    transport: "webrtc",
  },
  {
    kind: "pipecat_voice",
    label: "Voice (Pipecat Cloud Daily)",
    modality: "voice",
    provider: "pipecat_cloud",
    transport: "webrtc",
  },
  {
    kind: "pipecat_chat",
    label: "Chat (Pipecat Cloud WebSocket)",
    modality: "text",
    provider: "pipecat_cloud",
    transport: "websocket",
  },
];

function interfaceOptions(
  capabilities: readonly ConnectionCapability[],
): InterfaceOption[] {
  return interfaceOptionCatalog.filter((option) =>
    capabilities.some(
      (capability) =>
        capability.transport === option.transport &&
        capability.provider === option.provider &&
        capability.modality === option.modality &&
        capability.initiators.includes("marker") &&
        (option.kind !== "websocket_voice" ||
          ((capability.codecs ?? []).length > 0 &&
            (capability.sample_rates_hz ?? []).length > 0)),
    ),
  );
}

// What the drawer shows: create form, resolved editable edit subject,
// deep-linked subject still resolving (or being stripped as non-editable),
// or the not-found / cross-agent error state.
export type ConnectionDrawerState =
  | { connection: ConnectionResponse; mode: "edit" }
  | { mode: "create" }
  | { mode: "loading" }
  | { message: string; mode: "error" };

export interface AgentConnectionsProps {
  agentId: string;
  editingConnectionId: string | null;
  isCreateOpen: boolean;
  onCloseEditor: () => void;
  onOpenCreate: () => void;
  onOpenEdit: (connectionId: string) => void;
}

export function AgentConnections({
  agentId,
  editingConnectionId,
  isCreateOpen,
  onCloseEditor,
  onOpenCreate,
  onOpenEdit,
}: AgentConnectionsProps) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [deleteConnectionId, setDeleteConnectionId] = useState<string | null>(
    null,
  );

  const connections = useListQuery("/v1/connections", {
    params: { agent_id: agentId },
    queryKey: queryKeys.connections.list({ agent_id: agentId }),
  });

  // The server projects only live cells for the active install profile.
  const me = useQuery({
    queryKey: queryKeys.me,
    queryFn: async () => {
      const { data, error } = await apiClient.GET("/v1/me");
      if (error) {
        throw new Error(requestErrorDescription(error));
      }
      return data;
    },
  });
  const connectionCapabilities = me.data?.connection_capabilities ?? [];

  const connectionDetail = useQuery({
    enabled: editingConnectionId != null,
    queryKey: queryKeys.connections.detail(editingConnectionId ?? "pending"),
    queryFn: async () => {
      if (editingConnectionId == null) {
        throw new Error("Connection id is required.");
      }
      const { data, error } = await apiClient.GET("/v1/connections/{id}", {
        params: { path: { id: editingConnectionId } },
      });
      if (error) {
        throw new ApiRequestError(error);
      }
      return data;
    },
    initialData: () =>
      queryClient
        .getQueryData<InfiniteData<ListQueryPage<"/v1/connections">>>(
          queryKeys.connections.list({ agent_id: agentId }),
        )
        ?.pages.flatMap((page) => page.items)
        .find((connection) => connection.id === editingConnectionId),
  });

  // A `?connection` uuid whose connection is not editable (websocket/sip/
  // unknown config) is treated as invalid → the param is stripped (agent.md).
  const subject = connectionDetail.data;
  useEffect(() => {
    if (
      editingConnectionId != null &&
      subject !== undefined &&
      subject.id === editingConnectionId &&
      subject.agent_id === agentId &&
      !isEditableConnection(subject)
    ) {
      onCloseEditor();
    }
  }, [agentId, editingConnectionId, onCloseEditor, subject]);

  const drawer: ConnectionDrawerState | null = isCreateOpen
    ? { mode: "create" }
    : editingConnectionId == null
      ? null
      : subject !== undefined
        ? subject.agent_id !== agentId
          ? { message: "Interface not found for this agent.", mode: "error" }
          : isEditableConnection(subject)
            ? { connection: subject, mode: "edit" }
            : // Non-editable: transient — the effect above strips the param.
              { mode: "loading" }
        : connectionDetail.isError
          ? {
              message: requestErrorDescription(connectionDetail.error),
              mode: "error",
            }
          : { mode: "loading" };

  const saveConnection = useMutation({
    mutationFn: async ({
      connection,
      submission,
    }: {
      connection: ConnectionResponse | null;
      submission: ConnectionFormSubmission;
    }) => {
      if (connection) {
        const body = {
          config: submission.config,
          display_name: submission.displayName,
          initiator: submission.initiator,
          transport: submission.transport,
        } satisfies ConnectionPatch;
        const { data, error } = await apiClient.PATCH("/v1/connections/{id}", {
          body,
          params: { path: { id: connection.id } },
        });
        if (error) {
          throw new ApiRequestError(error);
        }
        return data;
      }

      const body = {
        agent_id: agentId,
        config: submission.config,
        display_name: submission.displayName,
        initiator: submission.initiator,
        transport: submission.transport,
      } satisfies ConnectionCreate;
      const { data, error } = await apiClient.POST("/v1/connections", {
        body,
      });
      if (error) {
        throw new ApiRequestError(error);
      }
      return data;
    },
    onSuccess: async (connection) => {
      onCloseEditor();
      queryClient.setQueryData(
        queryKeys.connections.detail(connection.id),
        connection,
      );
      toast.success("Interface saved");
      await queryClient.invalidateQueries({
        queryKey: queryKeys.connections.list({ agent_id: agentId }),
      });
    },
    onError: (error) => {
      toast.error("Interface save failed", {
        description: requestErrorDescription(error),
      });
    },
  });

  const deleteConnection = useMutation({
    mutationFn: async (connection: ConnectionResponse) => {
      const { error } = await apiClient.DELETE("/v1/connections/{id}", {
        params: { path: { id: connection.id } },
      });
      if (error) {
        throw new ApiRequestError(error);
      }
      return connection;
    },
    onSuccess: async () => {
      setDeleteConnectionId(null);
      toast.success("Interface deleted");
      await queryClient.invalidateQueries({
        queryKey: queryKeys.connections.list({ agent_id: agentId }),
      });
    },
    onError: (error) => {
      // The inline confirm stays open (deleteConnectionId untouched) so the
      // operator can retry; the row renders the InlineError.
      toast.error("Interface delete failed", {
        description: requestErrorDescription(error),
      });
    },
  });

  return (
    <AgentConnectionsView
      connections={connections}
      deleteConnectionId={deleteConnectionId}
      deleteError={
        deleteConnection.isError
          ? requestErrorDescription(deleteConnection.error)
          : null
      }
      drawer={drawer}
      isDeletePending={deleteConnection.isPending}
      isSavePending={saveConnection.isPending}
      onCancelDelete={() => {
        deleteConnection.reset();
        setDeleteConnectionId(null);
      }}
      onCloseEditor={onCloseEditor}
      onConfirmDelete={(connection) => {
        deleteConnection.mutate(connection);
      }}
      onOpenCreate={onOpenCreate}
      onOpenEdit={onOpenEdit}
      onSave={async (submission) => {
        await saveConnection.mutateAsync({
          connection: drawer?.mode === "edit" ? drawer.connection : null,
          submission,
        });
      }}
      onStartDelete={(connection) => {
        deleteConnection.reset();
        setDeleteConnectionId(connection.id);
      }}
      connectionCapabilities={connectionCapabilities}
      saveError={
        saveConnection.isError
          ? requestErrorDescription(saveConnection.error)
          : null
      }
    />
  );
}

export interface AgentConnectionsViewProps {
  connections: ListQueryResult<ConnectionResponse>;
  deleteConnectionId: string | null;
  deleteError: string | null;
  drawer: ConnectionDrawerState | null;
  isDeletePending: boolean;
  isSavePending: boolean;
  onCancelDelete: () => void;
  onCloseEditor: () => void;
  onConfirmDelete: (connection: ConnectionResponse) => void;
  onOpenCreate: () => void;
  onOpenEdit: (connectionId: string) => void;
  onSave: (submission: ConnectionFormSubmission) => Promise<void>;
  onStartDelete: (connection: ConnectionResponse) => void;
  connectionCapabilities: ConnectionCapability[];
  saveError: string | null;
}

export function AgentConnectionsView({
  connections,
  deleteConnectionId,
  deleteError,
  drawer,
  isDeletePending,
  isSavePending,
  onCancelDelete,
  onCloseEditor,
  onConfirmDelete,
  onOpenCreate,
  onOpenEdit,
  onSave,
  onStartDelete,
  connectionCapabilities,
  saveError,
}: AgentConnectionsViewProps) {
  const columns = useMemo(
    () =>
      connectionColumns({
        deleteConnectionId,
        deleteError,
        isDeletePending,
        onCancelDelete,
        onConfirmDelete,
        onOpenEdit,
        onStartDelete,
      }),
    [
      deleteConnectionId,
      deleteError,
      isDeletePending,
      onCancelDelete,
      onConfirmDelete,
      onOpenEdit,
      onStartDelete,
    ],
  );

  return (
    <>
      <Section
        heading="Interfaces"
        actions={
          isShowcaseMode ? (
            <ShowcaseReadOnlyNotice />
          ) : (
            <Button size="sm" variant="secondary" onClick={onOpenCreate}>
              Add interface
            </Button>
          )
        }
      >
        <PaginatedTableSection
          ariaLabel="Interfaces"
          className="marker-agent-table"
          columns={columns}
          emptyState={<EmptyState title="No interfaces" />}
          errorMessage={connections.errorMessage}
          getRowId={(connection) => connection.id}
          hasNextPage={connections.hasNextPage}
          isFetchingNextPage={connections.isFetchingNextPage}
          isPending={connections.isPending}
          items={connections.items}
          listStateKey={connections.listStateKey}
          onLoadMore={connections.fetchNextPage}
          skeletonLabel="Loading interfaces"
          skeletonRows={3}
        />
      </Section>
      {drawer ? (
        <Drawer
          ariaLabel={
            drawer.mode === "create" ? "Add interface" : "Edit interface"
          }
          isOpen
          size="lg"
          onDismiss={onCloseEditor}
        >
          {drawer.mode === "loading" ? (
            <Skeleton label="Loading interface" rows={5} shape="detail-field" />
          ) : drawer.mode === "error" ? (
            <Stack gap={4}>
              <Text variant="caps">Edit interface</Text>
              <InlineError>{drawer.message}</InlineError>
              <Inline>
                <Button
                  type="button"
                  variant="secondary"
                  onClick={onCloseEditor}
                >
                  Close
                </Button>
              </Inline>
            </Stack>
          ) : (
            <ConnectionEditor
              connection={drawer.mode === "edit" ? drawer.connection : null}
              error={saveError}
              isPending={isSavePending}
              onCancel={onCloseEditor}
              onSubmit={onSave}
              connectionCapabilities={connectionCapabilities}
            />
          )}
        </Drawer>
      ) : null}
    </>
  );
}

interface ConnectionColumnHandlers {
  deleteConnectionId: string | null;
  deleteError: string | null;
  isDeletePending: boolean;
  onCancelDelete: () => void;
  onConfirmDelete: (connection: ConnectionResponse) => void;
  onOpenEdit: (connectionId: string) => void;
  onStartDelete: (connection: ConnectionResponse) => void;
}

function connectionColumns(
  handlers: ConnectionColumnHandlers,
): Array<ColumnDef<ConnectionResponse>> {
  return [
    {
      header: "Name",
      cell: ({ row }) => (
        <NameWithId
          id={row.original.id}
          idLabel="Interface ID"
          name={row.original.display_name}
          strong
        />
      ),
    },
    {
      header: "Interface",
      cell: ({ row }) => (
        <Badge tone={isRunningInterface(row.original) ? "success" : "warning"}>
          {interfaceLabel(row.original)}
        </Badge>
      ),
    },
    {
      header: "Target",
      cell: ({ row }) => targetLabel(row.original),
    },
    {
      header: "Created",
      cell: ({ row }) => <DateCell value={row.original.created_at} />,
    },
    {
      header: "Updated",
      cell: ({ row }) => <DateCell value={row.original.updated_at} />,
    },
    ...(!isShowcaseMode
      ? [
          {
            header: "Actions",
            cell: ({ row }) => (
              <ConnectionRowActions
                connection={row.original}
                deleteError={handlers.deleteError}
                isConfirmingDelete={
                  handlers.deleteConnectionId === row.original.id
                }
                isDeletePending={handlers.isDeletePending}
                onCancelDelete={handlers.onCancelDelete}
                onConfirmDelete={handlers.onConfirmDelete}
                onOpenEdit={handlers.onOpenEdit}
                onStartDelete={handlers.onStartDelete}
              />
            ),
          } satisfies ColumnDef<ConnectionResponse>,
        ]
      : []),
  ];
}

interface ConnectionRowActionsProps {
  connection: ConnectionResponse;
  deleteError: string | null;
  isConfirmingDelete: boolean;
  isDeletePending: boolean;
  onCancelDelete: () => void;
  onConfirmDelete: (connection: ConnectionResponse) => void;
  onOpenEdit: (connectionId: string) => void;
  onStartDelete: (connection: ConnectionResponse) => void;
}

function ConnectionRowActions({
  connection,
  deleteError,
  isConfirmingDelete,
  isDeletePending,
  onCancelDelete,
  onConfirmDelete,
  onOpenEdit,
  onStartDelete,
}: ConnectionRowActionsProps) {
  const confirm = (
    <RowConfirm
      confirmLabel="Confirm delete"
      isConfirming={isConfirmingDelete}
      isPending={isConfirmingDelete && isDeletePending}
      isTriggerDisabled={isDeletePending}
      onCancel={onCancelDelete}
      onConfirm={() => {
        onConfirmDelete(connection);
      }}
      onStart={() => {
        onStartDelete(connection);
      }}
      pendingLabel="Deleting..."
      sentence={`Delete ${connection.display_name}? This removes the interface from this agent.`}
      triggerLabel="Delete"
    />
  );

  if (isConfirmingDelete) {
    return (
      <Stack gap={2}>
        {confirm}
        {deleteError ? <InlineError>{deleteError}</InlineError> : null}
      </Stack>
    );
  }

  return (
    <Inline>
      {isEditableConnection(connection) ? (
        <Button
          size="sm"
          variant="secondary"
          onClick={() => {
            onOpenEdit(connection.id);
          }}
        >
          Edit
        </Button>
      ) : null}
      {confirm}
    </Inline>
  );
}

interface ConnectionEditorProps {
  // null = create; a resolved editable connection = edit.
  connection: ConnectionResponse | null;
  error: string | null;
  isPending: boolean;
  onCancel: () => void;
  onSubmit: (submission: ConnectionFormSubmission) => Promise<void>;
  connectionCapabilities: ConnectionCapability[];
}

export function ConnectionEditor(props: ConnectionEditorProps) {
  return (
    <ConnectionEditorForm key={props.connection?.id ?? "new"} {...props} />
  );
}

function ConnectionEditorForm({
  connection,
  error,
  isPending,
  onCancel,
  onSubmit,
  connectionCapabilities,
}: ConnectionEditorProps) {
  const [draft, setDraft] = useState(() =>
    connection ? draftFromConnection(connection) : emptyDraft(),
  );
  const [localError, setLocalError] = useState<string | null>(null);
  const storedConfig = connection ? connection.config : null;
  const websocketVoiceCapability = connectionCapabilities.find(
    (capability) =>
      capability.transport === "websocket" &&
      capability.provider === "direct" &&
      capability.modality === "voice" &&
      capability.initiators.includes("marker"),
  );

  async function handleSubmit(event: SyntheticEvent<HTMLFormElement>) {
    event.preventDefault();
    const submission = buildConnectionSubmission(
      draft,
      websocketVoiceCapability,
    );
    if (!submission.ok) {
      setLocalError(submission.error);
      return;
    }
    setLocalError(null);
    try {
      await onSubmit(submission.value);
    } catch (caught) {
      setLocalError(requestErrorDescription(caught));
    }
  }

  return (
    <Stack gap={4}>
      <Text variant="caps">
        {connection ? "Edit interface" : "Add interface"}
      </Text>
      <form
        onSubmit={(event) => {
          void handleSubmit(event);
        }}
      >
        <Stack gap={4}>
          <TextInput
            label="Display name"
            required
            value={draft.displayName}
            onChange={(event) => {
              setDraft({ ...draft, displayName: event.currentTarget.value });
            }}
          />
          <Select
            label="Interface"
            required
            value={draft.interfaceKind}
            onChange={(event) => {
              setDraft({
                ...draft,
                interfaceKind: event.currentTarget.value as InterfaceKind,
              });
            }}
          >
            {interfaceOptions(connectionCapabilities).map((option) => (
              <option key={option.kind} value={option.kind}>
                {option.label}
              </option>
            ))}
          </Select>

          {draft.interfaceKind === "http" ? (
            <HttpInterfaceFields draft={draft} onDraftChange={setDraft} />
          ) : draft.interfaceKind === "websocket_chat" ||
            draft.interfaceKind === "websocket_voice" ? (
            <WebsocketInterfaceFields
              draft={draft}
              onDraftChange={setDraft}
              voiceCapability={websocketVoiceCapability}
            />
          ) : draft.interfaceKind === "twilio" ? (
            <TelephonyInterfaceFields draft={draft} onDraftChange={setDraft} />
          ) : draft.interfaceKind === "pipecat_voice" ||
            draft.interfaceKind === "pipecat_chat" ? (
            <PipecatCloudInterfaceFields
              draft={draft}
              onDraftChange={setDraft}
            />
          ) : (
            <LiveKitInterfaceFields draft={draft} onDraftChange={setDraft} />
          )}

          <Stack gap={2}>
            <Text variant="caps">Stored config JSON</Text>
            <Code block>
              {JSON.stringify(storedConfig ?? configPreview(draft), null, 2)}
            </Code>
          </Stack>

          {(localError ?? error) ? (
            <InlineError>{localError ?? error}</InlineError>
          ) : null}
          <Inline>
            <Button disabled={isPending} type="submit">
              {isPending ? "Saving..." : "Save"}
            </Button>
            <Button
              disabled={isPending}
              type="button"
              variant="secondary"
              onClick={onCancel}
            >
              Cancel
            </Button>
          </Inline>
        </Stack>
      </form>
    </Stack>
  );
}
